import { Button, Divider } from '@material-ui/core';
import { colors, Icon, IconButton } from '@mui/material';
import { httpApi } from '@nucleus-care/nucleuscare-backend-client';
import { useModal } from 'components/UI';
import { useState, useEffect } from 'react';
import { Spinner } from 'react-activity';
import AuthStore from 'stores/AuthStore';
import { UserConfig } from 'stores/UserStore';
import styled from 'styled-components';
import { PatientDetail, patientDetailStore, PatientLink } from 'zustand/patientDetailStore';

const Container = styled.div`
  border-radius: 8px;
  position: relative;
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => (props.isOpen ? 'block' : 'none')};
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.5);
  padding-top: 60px;
`;

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: 5% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 80%;
  max-width: 500px;
  border-radius: 8px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px;
  margin: 0px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const FullWidthContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ErrorText = styled.span`
  color: red;
  font-size: 10px;
  display: block;
  margin-top: 0px;
  margin-bottom: 10px;
`;

export const PatientLinks = ({ patientDetails, onReload }: { patientDetails: PatientDetail | undefined, onReload: () => void }) => {
  const { isOpen, openModal, closeModal } = useModal();
  const userId = AuthStore.getUserID();
  const [userInfo, setUserInfo] = useState<UserConfig | undefined>();
  const [newLink, setNewLink] = useState<PatientLink>({ id: '', label: '', url: '' });

  const { fetch: fetchPatientDetails, createPatientLink, deletePatientLink, addingLink, removingLink, fetching, setDetails } = patientDetailStore(store => store);

  const fetchUserConfig = () => {
    httpApi
      .get(`/user/${userId}/config`)
      .then(({ data }) => {
        setUserInfo(data);
      })
      .catch(e => {
        console.warn('Error getting user Information', e);
      });
  };

  useEffect(() => {
    fetchUserConfig();
  }, []);

  useEffect(() => {
    setDetails(patientDetails);
  }, [patientDetails]);

  useEffect(() => {
    if (!isOpen) {
      setNewLink({ id: '', label: '', url: '' });
    }
    return () => {
      setNewLink({ id: '', label: '', url: '' });
    };
  }, [isOpen]);

  const isEditable = userInfo?.CanEditPatientLinks;

  const isLoading = addingLink || removingLink || fetching;

  const onSaveLink = async () => {
    await createPatientLink(newLink);
    onReload();
    closeModal();
  };

  const isLinkValid = /^(https?:\/\/)/.test(newLink.url);

  return (
    <Container>
      <Modal isOpen={isOpen}>
        <ModalContent>
          <h5>Patient Links</h5>
          <TextInput placeholder="Label" value={newLink.label} onChange={e => setNewLink({ ...newLink, label: e.target.value })} disabled={!isEditable} />
          <TextInput placeholder="URL" value={newLink.url} onChange={e => setNewLink({ ...newLink, url: e.target.value })} disabled={!isEditable} />
          {!isLinkValid && newLink.url  && <ErrorText>Please enter a valid URL starting with http:// or https://</ErrorText>}
          <FullWidthContainer>
            <Button variant="outlined" onClick={closeModal}>
              Close
            </Button>

            <Button disabled={isLoading || !isLinkValid} variant="contained" onClick={onSaveLink}>
              {isLoading ? <Spinner /> : 'Add'}
            </Button>
          </FullWidthContainer>
          <Divider style={{ margin: '20px 0' }} />
          <h5>Existing Links</h5>
          <div>
            {patientDetails?.Links?.map((link, index) => (
              <div key={index} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Button
                  variant="outlined"
                  color="default"
                  disabled={isLoading}
                  style={{ marginLeft: '10px', borderColor: colors.red[300] }}
                  onClick={async () => {
                    // Logic to delete the link can be added here
                    await deletePatientLink(link.id);
                    onReload();
                  }}
                >
                  Remove
                </Button>
                <span
                  style={{
                    fontSize: '16px',
                    color: '#333',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {link.label}({link.url})
                </span>
              </div>
            ))}
          </div>
        </ModalContent>
      </Modal>

      {isEditable && (
        <Button variant="outlined" onClick={openModal}>
          Manage Links
        </Button>
      )}
    </Container>
  );
};
